import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useEffect, useRef, useState } from 'react';
import type { UpwindResource, UpwindResourcesResponse } from './upwindTypes';

type State = { loading: boolean; error?: Error; value?: UpwindResourcesResponse };

export function useUpwindResources(): State {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const entityUid = entity.metadata.uid;

  const apisRef = useRef({ discoveryApi, fetchApi });
  apisRef.current = { discoveryApi, fetchApi };

  const [state, setState] = useState<State>({ loading: true });

  useEffect(() => {
    if (!entityUid) {
      setState({ loading: false, value: { resources: [] } });
      return () => {};
    }

    let cancelled = false;
    const resources: UpwindResource[] = [];
    setState({ loading: true });

    const run = async () => {
      const { discoveryApi: dApi, fetchApi: fApi } = apisRef.current;
      const base = await dApi.getBaseUrl('upwind');
      const res = await fApi.fetch(`${base}/resources/${encodeURIComponent(entityUid)}`);

      if (res.status === 429) throw new Error('Upwind API rate limit exceeded — try again in a moment');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      if (!res.body) throw new Error('Empty response body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (cancelled) return;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          const parsed = JSON.parse(trimmed);
          if (parsed.__error) throw new Error('Stream interrupted — partial data may be shown');
          resources.push(parsed as UpwindResource);
        }

        if (!cancelled) setState({ loading: true, value: { resources: [...resources] } });
      }

      // Flush any data remaining in the buffer after the stream closes.
      buffer += decoder.decode();
      const remaining = buffer.trim();
      if (remaining) {
        const parsed = JSON.parse(remaining);
        if (parsed.__error) throw new Error('Stream interrupted — partial data may be shown');
        resources.push(parsed as UpwindResource);
      }

      if (!cancelled) setState({ loading: false, value: { resources } });
    };

    run().catch(err => {
      if (!cancelled) {
        setState({
          loading: false,
          value: resources.length ? { resources } : undefined,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    });

    return () => { cancelled = true; };
  }, [entityUid]);

  return state;
}
